import { CellContext, createColumnHelper } from '@tanstack/react-table';

import { Badge } from '@/react/components/Badge';

import {
  NetworkSecurityRule,
  PodSelection,
  PodSelectionType,
  TrafficSourceType,
} from './types';

export function useColumns(ruleKind: 'ingress' | 'egress') {
  const columnHelper = createColumnHelper<NetworkSecurityRule>();
  const namespaceHeader =
    ruleKind === 'ingress' ? 'From Namespace' : 'To Namespace';

  return [
    columnHelper.accessor('name', {
      header: 'Name',
      id: 'name',
      cell: NameCell,
    }),
    columnHelper.accessor('appliesTo', {
      header: 'Applies To',
      id: 'appliesTo',
      cell: AppliesToCell,
    }),
    columnHelper.accessor('namespace', {
      header: namespaceHeader,
      id: 'namespace',
    }),
    columnHelper.accessor('podLabels', {
      header: 'Pod Labels',
      id: 'podLabels',
      cell: PodLabelsCell,
    }),
    columnHelper.accessor('port', {
      header: 'Port',
      id: 'port',
      cell: PortCell,
    }),
  ];
}

function NameCell({
  getValue,
  row: { original: rule },
}: CellContext<NetworkSecurityRule, string | undefined>) {
  const name = getValue();
  if (!name) {
    return <span></span>;
  }
  const isAllow = rule.sourceType !== TrafficSourceType.DenyAll;
  return (
    <span>
      {name}{' '}
      <Badge type={isAllow ? 'success' : 'danger'}>
        {isAllow ? 'ALLOW' : 'DENY'}
      </Badge>
    </span>
  );
}

function AppliesToCell({
  getValue,
}: CellContext<NetworkSecurityRule, PodSelection | undefined>) {
  const v = getValue();
  if (!v) {
    return null;
  }
  const matchLabels: string[] = [];
  const matchExpressions: string[] = [];
  switch (v.type) {
    case PodSelectionType.All:
      return <span>All pods</span>;
    case PodSelectionType.MatchLabels:
      if (v.matchLabels) {
        for (const key of Object.keys(v.matchLabels)) {
          matchLabels.push(key + ': ' + v.matchLabels[key]);
        }
      }
      return <span>Labels: {matchLabels.join(', ')}</span>;
    case PodSelectionType.MatchExpression:
      if (v.matchExpressions) {
        for (const label of v.matchExpressions) {
          matchExpressions.push(
            label.key +
              ' ' +
              label.operator +
              ': (' +
              label.values.join(', ') +
              ')'
          );
        }
      }
      return <span>Match Expressions: {matchExpressions.join(', ')}</span>;
    default:
      return <span>Unknown</span>;
  }
}

function PodLabelsCell({
  getValue,
}: CellContext<NetworkSecurityRule, Record<string, string>>) {
  const labels = getValue();
  const t: string[] = [];
  if (labels) {
    for (const key of Object.keys(labels)) {
      t.push(key + ': ' + labels[key]);
    }
  }
  return t.join(', ');
}

function PortCell({
  getValue,
  row: { original: rule },
}: CellContext<NetworkSecurityRule, number>) {
  const port = getValue();
  let text = '';
  if (port !== undefined) {
    const protocol = rule.protocol ? rule.protocol : 'TCP';
    text += port + '/' + protocol;
  }
  return text;
}
